import { GiftFilled, StarFilled } from '@ant-design/icons';
import anniversaryBackground from 'assets/aniversary-bg.svg';
import birthdayBackground from 'assets/birthday-bg.svg';
import { Card, Col, Empty, Row, Skeleton } from 'antd';
import RenderPhotos from 'components/employees/profile/RenderPhotos';
import { endOfWeek, format, isWithinInterval, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { SliceStatus } from 'constants/enums/slices';
import { getTodayFormattedDate } from 'hooks/formatDate';
import { UpcomingEmployeeEvent } from 'types/user';
import { usersPaths } from 'constants/routePaths';
import { Link } from 'react-router';

type UpcomingEventsWidgetKind = 'birthday' | 'anniversary';

interface UpcomingEventsWidgetProps {
  title: string;
  kind: UpcomingEventsWidgetKind;
  dataCy: string;
  events: UpcomingEmployeeEvent[];
  status: SliceStatus;
  emptyMessage: string;
  errorMessage: string;
}

type UpcomingEventsSections = {
  today: UpcomingEmployeeEvent[];
  thisWeek: UpcomingEmployeeEvent[];
  nextWeek: UpcomingEmployeeEvent[];
};

const WIDGET_BACKGROUND_BY_KIND = {
  anniversary: anniversaryBackground,
  birthday: birthdayBackground,
} as const;

const getDashboardReferenceDate = () =>
  parseISO(getTodayFormattedDate({ format: 'yyyy-MM-dd' }));

const formatEventDateLabel = (date: Date | string) =>
  format(typeof date === 'string' ? parseISO(date) : date, 'd MMM', {
    locale: es,
  }).replace('.', '');

const getEventSections = (
  events: UpcomingEmployeeEvent[]
): UpcomingEventsSections => {
  const today = getDashboardReferenceDate();
  const endOfCurrentWeek = endOfWeek(today, { weekStartsOn: 1 });

  return events.reduce<UpcomingEventsSections>(
    (acc, event) => {
      const eventDate = parseISO(event.eventDate);

      if (event.daysUntil === 0) {
        acc.today.push(event);
        return acc;
      }

      if (
        isWithinInterval(eventDate, { start: today, end: endOfCurrentWeek })
      ) {
        acc.thisWeek.push(event);
        return acc;
      }

      acc.nextWeek.push(event);
      return acc;
    },
    { today: [], thisWeek: [], nextWeek: [] }
  );
};

const UpcomingEventRow = ({
  dataCy,
  event,
  showYears,
}: {
  dataCy: string;
  event: UpcomingEmployeeEvent;
  showYears: boolean;
}) => (
  <Row
    align="middle"
    className="dashboard-upcoming-events-row"
    data-cy={dataCy}
    style={{ display: 'flex' }}
    wrap={false}
  >
    <Col flex="20px" className="dashboard-upcoming-events-row__avatar">
      <RenderPhotos
        employeeId={event.idEmployee}
        lastName={event.lastName}
        name={event.name}
        size="xs"
      />
    </Col>
    <Col flex="auto" className="dashboard-upcoming-events-row__content">
      <Link
        className="dashboard-employee-link dashboard-upcoming-events-row__name"
        data-cy={`dashboard-employee-link-${event.userId}`}
        title={event.fullName}
        to={`${usersPaths.basePath}/${usersPaths.userDetail}/${event.userId}`}
      >
        {event.fullName}
      </Link>
      {showYears && event.years > 0 && (
        <span className="dashboard-upcoming-events-row__badge">
          {event.years}
        </span>
      )}
    </Col>
    <Col className="dashboard-upcoming-events-row__date" flex="none">
      {formatEventDateLabel(event.eventDate)}
    </Col>
  </Row>
);

const UpcomingEventsSection = ({
  title,
  events,
  showYears,
  dataCy,
  emptySectionMessage,
}: {
  title: string;
  events: UpcomingEmployeeEvent[];
  showYears: boolean;
  dataCy: string;
  emptySectionMessage: string;
}) => (
  <div className="dashboard-upcoming-events-widget__column" data-cy={dataCy}>
    <p className="dashboard-upcoming-events-widget__section-title">{title}</p>
    <div className="dashboard-upcoming-events-widget__column-content">
      {events.length ? (
        events.map((event) => (
          <UpcomingEventRow
            dataCy={`${dataCy}-row-${event.userId}`}
            event={event}
            key={`${dataCy}-${event.userId}-${event.eventDate}`}
            showYears={showYears}
          />
        ))
      ) : (
        <div className="dashboard-upcoming-events-widget__section-empty">
          {emptySectionMessage}
        </div>
      )}
    </div>
  </div>
);

export const UpcomingEventsWidget = ({
  title,
  kind,
  dataCy,
  events,
  status,
  emptyMessage,
  errorMessage,
}: UpcomingEventsWidgetProps) => {
  const showYears = kind === 'anniversary';
  const backgroundImage = WIDGET_BACKGROUND_BY_KIND[kind];
  const emptySectionMessage = showYears
    ? 'No hay aniversarios'
    : 'No hay cumpleaños';
  const filteredEvents = showYears
    ? events.filter((event) => event.years > 0)
    : events;
  const { today, thisWeek, nextWeek } = getEventSections(filteredEvents);
  const todayLabel = formatEventDateLabel(getDashboardReferenceDate());

  const Icon = showYears ? StarFilled : GiftFilled;

  return (
    <div data-cy={dataCy}>
      <Card className={`card dashboard-upcoming-events-widget ${dataCy}`}>
        <img
          alt=""
          aria-hidden
          className={`dashboard-upcoming-events-widget__art dashboard-upcoming-events-widget__art--${kind}`}
          src={backgroundImage}
        />
        <div className="dashboard-upcoming-events-widget__content">
          <h3 className="sub-title">{title}</h3>

          {(status === SliceStatus.Empty ||
            status === SliceStatus.Updating) && (
            <div
              className="dashboard-upcoming-events-widget__state"
              data-cy={`${dataCy}-loading-state`}
            >
              <Skeleton
                active
                paragraph={{ rows: 8 }}
                title={{ width: '45%' }}
              />
            </div>
          )}

          {status === SliceStatus.Error && (
            <div
              className="dashboard-upcoming-events-widget__state dashboard-upcoming-events-widget__state--error"
              data-cy={`${dataCy}-error-state`}
            >
              <p>{errorMessage}</p>
              <span>Recarga la página para intentarlo de nuevo.</span>
            </div>
          )}

          {status === SliceStatus.Fulfilled && filteredEvents.length === 0 && (
            <div
              className="dashboard-upcoming-events-widget__state dashboard-upcoming-events-widget__state--empty"
              data-cy={`${dataCy}-empty-state`}
            >
              <Empty
                description={emptyMessage}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          )}

          {status === SliceStatus.Fulfilled && filteredEvents.length > 0 && (
            <div
              className="dashboard-upcoming-events-widget__lists"
              data-cy={`${dataCy}-lists`}
            >
              <div className="dashboard-upcoming-events-widget__primary-column">
                {today.length > 0 && (
                  <section
                    className="dashboard-upcoming-events-widget__today-card"
                    data-cy={`${dataCy}-today-section`}
                  >
                    <Row
                      align="middle"
                      className="dashboard-upcoming-events-widget__today-header"
                      wrap={false}
                    >
                      <Col flex="none">
                        <Icon />
                      </Col>
                      <Col flex="auto">{`Hoy ${todayLabel}`}</Col>
                    </Row>
                    <div className="dashboard-upcoming-events-widget__today-list">
                      {today.map((event) => (
                        <UpcomingEventRow
                          dataCy={`${dataCy}-today-row-${event.userId}`}
                          event={event}
                          key={`${dataCy}-today-${event.userId}-${event.eventDate}`}
                          showYears={showYears}
                        />
                      ))}
                    </div>
                  </section>
                )}
                <UpcomingEventsSection
                  dataCy={`${dataCy}-this-week`}
                  emptySectionMessage={emptySectionMessage}
                  events={thisWeek}
                  showYears={showYears}
                  title="Esta semana"
                />
              </div>
              <div className="dashboard-upcoming-events-widget__divider" />
              <UpcomingEventsSection
                dataCy={`${dataCy}-next-week`}
                emptySectionMessage={emptySectionMessage}
                events={nextWeek}
                showYears={showYears}
                title="Próxima semana"
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
